import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createHead } from '@vueuse/head' // Import createHead
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import Toast from 'vue-toastification'
import "vue-toastification/dist/index.css"
import App from './App.vue'
import router from './router'
import axios from 'axios'
import i18n from './i18n' // Import i18n configuration
import { useAuthStore } from './stores/authStore' // Import auth store
import { getUserLocale } from './utils/localeUtils'
import { logger } from './utils/logger' // Import logger utility
import './assets/css/animations.css'
import { initGA, trackPageView } from './utils/ga' // Import GA module

// Initialize Sentry for error tracking
import * as Sentry from "@sentry/vue";

// Get environment variables
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.MODE || 'development';
const appVersion = import.meta.env.PACKAGE_VERSION || '1.0.0';

// Initialize Pinia store
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate) // Add persistedstate plugin

// Create Vue application early so we can use stores
const app = createApp(App)
const head = createHead() // Create head instance
app.use(pinia)
app.use(head) // Use head plugin

// Initialize Sentry if DSN is available
if (sentryDsn) {
  console.log(`Initializing Sentry in ${environment} environment`);
  
  Sentry.init({
    app,
    dsn: sentryDsn,
    environment,
    release: `florist-frontend@${appVersion}`,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
        trackComponents: true,
      }),
      Sentry.replayIntegration({
        // Only capture errors and not all sessions
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Performance monitoring
    tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
    
    // Session replay for error diagnosis (limited)
    replaysSessionSampleRate: 0.0, // Regular sessions not captured
    replaysOnErrorSampleRate: 1.0,  // Error sessions captured
    
    // Enable Vue-specific error handling
    trackComponents: true,
    logErrors: true,
    
    // Add useful context to errors
    beforeSend(event) {
      // Add user information if available
      const authStore = useAuthStore();
      if (authStore && authStore.isAuthenticated) {
        event.user = {
          id: authStore.user?.id,
          email: authStore.user?.email,
          username: authStore.user?.username,
        };
      }
      
      // Add additional context
      event.tags = {
        ...event.tags,
        locale: getUserLocale(),
      };
      
      return event;
    },
  });
  
  console.log('Sentry initialized successfully');
} else {
  console.log('Sentry DSN not found, error tracking disabled');
}

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        console.log('Service Worker registered with scope:', registration.scope);
      })
      .catch(error => {
        console.error('Service Worker registration failed:', error);
      });
  });
}

// Setup for PWA installation
window.PWAInstall = {
  // Flag to indicate if we already have an installable PWA
  isInstallable: false,
  // Store the actual event
  deferredPrompt: null,
  // Callbacks to execute when the event is captured
  callbacks: [],
  // Use fallback prompt if needed
  useFallbackPrompt: false,
  // Check if user is accessing via browser (not PWA)
  shouldShowPrompt: function() {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        (window.navigator.standalone === true);
    const isPwaInstalled = localStorage.getItem('pwaInstalled') === 'true';
    
    // Don't show if already in PWA mode or already installed
    if (isStandalone || isPwaInstalled) {
      return false;
    }
    
    // Time-based throttling: only show once per hour
    const lastShownTime = localStorage.getItem('pwaPromptLastShown');
    if (lastShownTime) {
      const now = Date.now();
      const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
      const timeSinceLastShown = now - parseInt(lastShownTime);
      
      if (timeSinceLastShown < oneHour) {
        const minutesRemaining = Math.ceil((oneHour - timeSinceLastShown) / (60 * 1000));
        console.log(`[PWA] PWA prompt throttled in shouldShowPrompt. Will show again in ${minutesRemaining} minutes`);
        return false;
      }
    }
    
    // All checks passed - can show prompt
    return true;
  },
  // Add a callback to be executed when the install prompt is available
  onPromptAvailable: function(callback) {
    if (this.deferredPrompt) {
      // If we already have the prompt, execute callback immediately
      callback(this.deferredPrompt);
    } else {
      // Otherwise, store the callback for later
      this.callbacks.push(callback);
    }
  },
  // Force the prompt to show for testing (dev only)
  forcePrompt: function() {
    if (import.meta.env.DEV) {
      if (this.deferredPrompt) {
        // If we have a real prompt, use it
        // Force prompt by temporarily clearing installed flag
        localStorage.removeItem('pwaInstalled');
        return true;
      } else {
        console.log('[PWA] No real prompt available for testing, try again later');
        return false;
      }
    }
    return false;
  },
  // Show a fallback prompt that works on all devices
  showFallbackPrompt: function() {
    // Update the last shown timestamp to prevent immediate re-showing
    localStorage.setItem('pwaPromptLastShown', Date.now().toString());
    console.log('[PWA] Fallback prompt shown, timestamp updated for throttling');
    
    // Create a UI prompt that doesn't rely on beforeinstallprompt
    const promptDiv = document.createElement('div');
    promptDiv.style.position = 'fixed';
    promptDiv.style.bottom = '20px';
    promptDiv.style.left = '0';
    promptDiv.style.right = '0';
    promptDiv.style.display = 'flex';
    promptDiv.style.justifyContent = 'center';
    promptDiv.style.zIndex = '9999';
    
    const promptContent = document.createElement('div');
    promptContent.style.background = 'white';
    promptContent.style.padding = '20px';
    promptContent.style.borderRadius = '12px';
    promptContent.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
    promptContent.style.width = '90%';
    promptContent.style.maxWidth = '400px';
    promptContent.style.textAlign = 'center';
    promptContent.style.borderLeft = '5px solid #4CAF50';
    
    promptContent.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 12px;">
        <span style="font-size: 24px; margin-right: 10px;">🌱</span>
        <h3 style="margin: 0; color: #4CAF50; font-weight: bold;">Install PlantCare</h3>
      </div>
      <p style="margin: 8px 0 16px; color: #333; font-size: 14px; line-height: 1.5;">
        Add PlantCare to your home screen for better experience, offline access, and plant care reminders!
      </p>
      <div style="margin: 15px 0; padding: 10px; background-color: rgba(76, 175, 80, 0.08); border-radius: 8px;">
        <div style="display: flex; align-items: center; margin-bottom: 10px;">
          <span style="font-size: 18px; margin-right: 10px;">⚡</span>
          <span>Faster access</span>
        </div>
        <div style="display: flex; align-items: center; margin-bottom: 10px;">
          <span style="font-size: 18px; margin-right: 10px;">📱</span>
          <span>App-like experience</span>
        </div>
        <div style="display: flex; align-items: center;">
          <span style="font-size: 18px; margin-right: 10px;">🔔</span>
          <span>Get notifications</span>
        </div>
      </div>
      <div style="display: flex; justify-content: center; gap: 10px; margin-top: 15px;">
        <button id="pwa-install-button" style="background-color: #4CAF50; color: white; border: none; padding: 10px 20px; border-radius: 5px; font-weight: bold; cursor: pointer;">
          Install Now
        </button>
        <button id="pwa-dismiss-button" style="background-color: transparent; color: #666; border: 1px solid #ddd; padding: 10px 20px; border-radius: 5px; cursor: pointer;">
          Maybe Later
        </button>
      </div>
    `;
    
    promptDiv.appendChild(promptContent);
    document.body.appendChild(promptDiv);
    
    // Add event listeners to buttons
    document.getElementById('pwa-install-button').addEventListener('click', async function() {
      // If native prompt is available, use it
      if (window.PWAInstall.deferredPrompt && typeof window.PWAInstall.deferredPrompt.prompt === 'function') {
        try {
          window.PWAInstall.deferredPrompt.prompt();
          const { outcome } = await window.PWAInstall.deferredPrompt.userChoice;
          if (outcome === 'accepted') {
            localStorage.setItem('pwaInstalled', 'true');
          }
        } catch (e) {
          // fall back to instructions below
        }
      } else {
        // Enhanced platform and browser-specific instructions
        const ua = navigator.userAgent;
        let instructions = '';
        
        if (/iPhone|iPad|iPod/i.test(ua)) {
          // iOS Safari
          instructions = 'iOS Safari:\n\n1. Tap the Share button (□↗) at the bottom\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm\n\nThe app will appear on your home screen!';
        } else if (/Android/i.test(ua)) {
          if (/Chrome/i.test(ua)) {
            instructions = 'Android Chrome:\n\n1. Tap the menu (⋮) in the top-right\n2. Tap "Install app" or "Add to Home screen"\n3. Confirm the installation\n\nAlternatively, look for the install banner or + icon in the address bar.';
          } else if (/Firefox/i.test(ua)) {
            instructions = 'Android Firefox:\n\n1. Tap the menu (⋮) in the top-right\n2. Tap "Install"\n3. Confirm the installation\n\nThe app will be added to your home screen.';
          } else {
            instructions = 'Android:\n\n1. Look for an "Install" or "Add to Home Screen" option in your browser menu\n2. Or check for an install icon (+) in the address bar\n3. Follow the prompts to install\n\nNote: PWA support varies by browser.';
          }
        } else {
          // Desktop browsers
          if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) {
            instructions = 'Chrome Desktop:\n\n1. Look for the install (+) icon in the address bar\n2. Or click the menu (⋮) → "Install PlantCare..."\n3. Click "Install" to confirm\n\nThe app will appear in your applications folder and taskbar.';
          } else if (/Edg/i.test(ua)) {
            instructions = 'Microsoft Edge:\n\n1. Look for the install (+) icon in the address bar\n2. Or click the menu (⋯) → "Apps" → "Install this site as an app"\n3. Click "Install" to confirm\n\nThe app will be available in your Start menu and taskbar.';
          } else if (/Firefox/i.test(ua)) {
            instructions = 'Firefox Desktop:\n\n1. Click the menu (≡) in the top-right\n2. Look for "Install" or "Add to Home Screen" options\n3. Follow the installation prompts\n\nNote: PWA support in Firefox is experimental and may vary.';
          } else if (/Safari/i.test(ua)) {
            instructions = 'Safari Desktop:\n\nSafari on macOS doesn\'t fully support PWA installation.\n\nAlternatives:\n• Use Chrome or Edge for the best PWA experience\n• Bookmark this page for quick access\n• Add to Dock by dragging the URL';
          } else {
            instructions = 'Desktop Browser:\n\n1. Look for an install icon (+) in the address bar\n2. Or check your browser menu for "Install" or "Add to Home Screen"\n3. Follow the installation prompts\n\nFor the best experience, try Chrome or Edge.';
          }
        }
        
        alert(instructions);
      }
      document.body.removeChild(promptDiv);
    });
    
    document.getElementById('pwa-dismiss-button').addEventListener('click', function() {
      // No longer tracking prompt count - will show again on next visit
      
      document.body.removeChild(promptDiv);
    });
    
    console.log('[PWA] Showing fallback install prompt');
  }
};

// Listen for the beforeinstallprompt event
window.addEventListener('beforeinstallprompt', (e) => {
  console.log('[PWA] beforeinstallprompt event captured at the main.js level');
  
  // Prevent the mini-infobar from appearing on mobile
  e.preventDefault();
  
  // Store the event
  window.PWAInstall.deferredPrompt = e;
  window.PWAInstall.isInstallable = true;
  
  // Execute any registered callbacks
  window.PWAInstall.callbacks.forEach(callback => {
    try {
      callback(e);
    } catch (error) {
      console.error('[PWA] Error executing callback:', error);
    }
  });
  
  // Clear the callbacks after execution
  window.PWAInstall.callbacks = [];
});

// Check for PWA installability 5 seconds after page load
// If beforeinstallprompt hasn't fired by then, we'll use the fallback (Android auto), and rely on menu button elsewhere
window.addEventListener('load', () => {
  setTimeout(() => {
    // If we've never captured a beforeinstallprompt event
    if (!window.PWAInstall.deferredPrompt && !window.PWAInstall.useFallbackPrompt) {
      console.log('[PWA] No beforeinstallprompt event detected after timeout, enabling fallback');
      window.PWAInstall.useFallbackPrompt = true;
      
      // Enhanced fallback prompt for ALL modern browsers (not just Android/Chrome)
      const ua = navigator.userAgent;
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      
      // Comprehensive browser detection for PWA support
      const browserSupport = {
        isChrome: /Chrome/i.test(ua) && !/Edg/i.test(ua),
        isEdge: /Edg/i.test(ua),
        isFirefox: /Firefox/i.test(ua),
        isSafari: /Safari/i.test(ua) && !/Chrome/i.test(ua) && !/Edg/i.test(ua),
        isOpera: /OPR|Opera/i.test(ua),
        isChromiumBased: /Chrome|Edg|OPR|Opera/i.test(ua)
      };
      
      const platform = {
        isAndroid: /Android/i.test(ua),
        isIOS: /iPhone|iPad|iPod/i.test(ua),
        isDesktop: !isMobile,
        isMobile: isMobile
      };
      
      // Show fallback for most modern browsers that support PWA
      const shouldShowFallback = window.PWAInstall.shouldShowPrompt() && (
        browserSupport.isChromiumBased ||  // Chrome, Edge, Opera, etc.
        browserSupport.isFirefox ||        // Firefox with PWA support
        (browserSupport.isSafari && platform.isIOS) // iOS Safari
      );
      
      if (shouldShowFallback) {
        let browserName = 'browser';
        if (browserSupport.isChrome) browserName = 'Chrome';
        else if (browserSupport.isEdge) browserName = 'Edge';  
        else if (browserSupport.isFirefox) browserName = 'Firefox';
        else if (browserSupport.isSafari) browserName = 'Safari';
        else if (browserSupport.isOpera) browserName = 'Opera';
        
        const platformName = platform.isAndroid ? 'Android' : platform.isIOS ? 'iOS' : 'Desktop';
        
        console.log(`[PWA] Auto-showing fallback prompt for ${browserName} on ${platformName}`);
        setTimeout(() => {
          window.PWAInstall.showFallbackPrompt();
        }, 1500);
      } else {
        console.log('[PWA] Browser/platform not suitable for PWA installation prompts:', {
          browser: browserSupport,
          platform: platform,
          userAgent: ua
        });
      }
    }
  }, 5000);
});

// Add a small utility to reset the visit counter for testing PWA installation
if (import.meta.env.DEV) {
  // Reset PWA state function for development testing
  window.resetPWACounters = () => {
    localStorage.removeItem('pwaInstalled');
    localStorage.removeItem('lastPromptTime');
    console.log('[PWA] PWA state reset. Refresh the page to test installation prompt.');
    return 'PWA state reset successfully. Refresh the page to test installation prompt.';
  };
  
  // Toggle PWA installed state for testing
  window.togglePWAInstalled = () => {
    const isInstalled = localStorage.getItem('pwaInstalled') === 'true';
    if (isInstalled) {
      localStorage.removeItem('pwaInstalled');
      console.log('[PWA] PWA marked as not installed. Refresh to see install prompt.');
      return 'PWA marked as not installed. Refresh to see install prompt.';
    } else {
      localStorage.setItem('pwaInstalled', 'true');
      console.log('[PWA] PWA marked as installed. Refresh - prompt should not appear.');
      return 'PWA marked as installed. Refresh - prompt should not appear.';
    }
  };
  
  // Show install prompt for testing
  window.testInstallPrompt = () => {
    if (window.PWAInstall.forcePrompt()) {
      console.log('[PWA] Test mode: Forced prompt showing');
      return 'Install prompt should appear shortly';
    } else {
      return 'No install prompt available to show';
    }
  };
  
  console.log('[PWA] Development utilities added. Use window.resetPWACounters(), window.togglePWAInstalled(), or window.testInstallPrompt() in console.');
}

// Add a button to force showing the install prompt for Android emulator testing
if (window.PWAInstall && import.meta.env.DEV) {
  window.showForcedPrompt = function() {
    // Create a UI prompt that doesn't rely on beforeinstallprompt
    const promptDiv = document.createElement('div');
    promptDiv.style.position = 'fixed';
    promptDiv.style.bottom = '20px';
    promptDiv.style.left = '0';
    promptDiv.style.right = '0';
    promptDiv.style.display = 'flex';
    promptDiv.style.justifyContent = 'center';
    promptDiv.style.zIndex = '9999';
    
    const promptContent = document.createElement('div');
    promptContent.style.background = 'white';
    promptContent.style.padding = '20px';
    promptContent.style.borderRadius = '12px';
    promptContent.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
    promptContent.style.width = '90%';
    promptContent.style.maxWidth = '400px';
    promptContent.style.textAlign = 'center';
    promptContent.style.borderLeft = '5px solid #4CAF50';
    
    promptContent.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 12px;">
        <span style="font-size: 24px; margin-right: 10px;">🌱</span>
        <h3 style="margin: 0; color: #4CAF50; font-weight: bold;">Install PlantCare</h3>
      </div>
      <p style="margin: 8px 0 16px; color: #333; font-size: 14px; line-height: 1.5;">
        Add PlantCare to your home screen for better experience, offline access, and plant care reminders!
      </p>
      <div style="margin: 15px 0; padding: 10px; background-color: rgba(76, 175, 80, 0.08); border-radius: 8px;">
        <div style="display: flex; align-items: center; margin-bottom: 10px;">
          <span style="font-size: 18px; margin-right: 10px;">⚡</span>
          <span>Faster access</span>
        </div>
        <div style="display: flex; align-items: center; margin-bottom: 10px;">
          <span style="font-size: 18px; margin-right: 10px;">📱</span>
          <span>App-like experience</span>
        </div>
        <div style="display: flex; align-items: center;">
          <span style="font-size: 18px; margin-right: 10px;">🔔</span>
          <span>Get notifications</span>
        </div>
      </div>
      <div style="display: flex; justify-content: center; gap: 10px; margin-top: 15px;">
        <button id="pwa-install-button" style="background-color: #4CAF50; color: white; border: none; padding: 10px 20px; border-radius: 5px; font-weight: bold; cursor: pointer;">
          Install Now
        </button>
        <button id="pwa-dismiss-button" style="background-color: transparent; color: #666; border: 1px solid #ddd; padding: 10px 20px; border-radius: 5px; cursor: pointer;">
          Maybe Later
        </button>
      </div>
    `;
    
    promptDiv.appendChild(promptContent);
    document.body.appendChild(promptDiv);
    
    // Add event listeners to buttons
    document.getElementById('pwa-install-button').addEventListener('click', function() {
      // Show installation instructions for Android
      alert('To install this app on Android:\n\n1. Tap the menu button (⋮) in Chrome\n2. Tap "Install app" or "Add to Home screen"\n\nThe app will then appear on your home screen.');
      document.body.removeChild(promptDiv);
    });
    
    document.getElementById('pwa-dismiss-button').addEventListener('click', function() {
      document.body.removeChild(promptDiv);
    });
    
    console.log('[PWA] Showing forced install prompt for Android emulator testing');
    return 'Forced prompt shown - this is for testing only';
  };
  
  console.log('[PWA] Added force prompt function. Use window.showForcedPrompt() in console to test.');
}

// Configure Vue Toastification
const toastOptions = {
  position: "top-right",
  timeout: 3000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: true,
  closeButton: "button",
  icon: true,
  rtl: false
}
app.use(Toast, toastOptions)

// Set up axios defaults
const apiBaseUrl = import.meta.env.VITE_BACKEND_URL;
axios.defaults.baseURL = apiBaseUrl;
logger.debug('Axios baseURL configured as:', apiBaseUrl);

// Add a simple timeout to avoid indefinite pending requests
axios.defaults.timeout = 15000; // 15 seconds

// Configure CORS settings for Cloudflare gateway
const parsedUrl = new URL(apiBaseUrl);
axios.defaults.withCredentials = false; // Disable by default for all requests
logger.debug('Credentials mode disabled by default for API requests');

// Fix CORS issues with headers that might trigger preflight requests
axios.interceptors.request.use(config => {
  // For all requests to the API domain, keep only essential headers
  if (config.url && !config.url.startsWith('http')) {
    // Remove any problematic headers that might trigger preflight CORS
    const safeHeaders = {
      'Authorization': config.headers['Authorization'],
      'Content-Type': config.headers['Content-Type'],
      'Accept': 'application/json'
    };
    
    // Only keep defined headers
    config.headers = Object.entries(safeHeaders)
      .filter(([_, value]) => value !== undefined)
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
  }
  
  // Always ensure withCredentials is false for API requests
  config.withCredentials = false;
  
  return config;
}, error => Promise.reject(error));

// Test the API connection
const testAPIConnection = async () => {
  try {
    logger.debug('Testing connection to API server...');
    // Use the correct health endpoint path
    await axios.get('/health', { 
      timeout: 5000,
      withCredentials: false
    });
    logger.debug('API server connection successful');
    console.log('%c✅ Backend API connection successful', 'color: green; font-weight: bold');
    window.isApiAccessible = true;
  } catch (error) {
    // Check for 404 error - might just mean health endpoint isn't implemented
    if (error.response && error.response.status === 404) {
      logger.debug('Health endpoint not found, but API seems to be reachable');
      console.log('%c✓ Backend API seems to be accessible', 'color: green');
      window.isApiAccessible = true;
      return;
    }
    
    logger.error('API server connection failed:', error.message);
    console.log('%c⚠️ Backend API connection failed', 'color: red; font-weight: bold');
    console.log('Please ensure the backend server is running at', apiBaseUrl);
    window.isApiAccessible = false;
  }
};

// Perform API connection test
testAPIConnection();

// Ensure that absolute URLs in axios are allowed
axios.defaults.allowAbsoluteUrls = true

// Function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const { exp } = JSON.parse(jsonPayload);
    const currentTime = Math.floor(Date.now() / 1000);
    
    return exp < currentTime;
  } catch (error) {
    logger.error('Error checking token expiration:', error);
    return true;
  }
};

// Set up axios interceptors for CORS handling
axios.interceptors.request.use(
  config => {
    // Don't send credentials for cross-origin requests to avoid CORS issues
    if (config.url && (config.url.startsWith('http') || config.url.startsWith('//'))) {
      config.withCredentials = false;
    }
    
    return config;
  },
  error => Promise.reject(error)
);

// Set up axios interceptors for request handling
axios.interceptors.request.use(
  async config => {
    const token = localStorage.getItem('token');
    
    if (token && isTokenExpired(token)) {
      logger.warn('Token is expired, clearing auth');
      const authStore = useAuthStore();
      authStore.clearAuth();
      
      if (router.currentRoute.value.path !== '/login') {
        router.push('/login');
      }
      
      throw new axios.Cancel('Operation canceled due to expired token');
    }
    
    // Add token to headers if it exists
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add minimal language preference headers using standard headers that won't cause CORS issues
    const locale = getUserLocale();
    config.headers['Accept-Language'] = locale;
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Set up axios interceptors for handling common responses
axios.interceptors.response.use(
  response => response,
  error => {
    // Handle common errors
    if (error.response) {
      // Handle 401 Unauthorized errors globally
      if (error.response.status === 401) {
        logger.warn('Authentication error, clearing token');
        
        const authStore = useAuthStore();
        if (authStore.isAuthenticated) {
          authStore.clearAuth();
          
          // Only redirect to login page if current route requires authentication
          const currentRoute = router.currentRoute.value;
          if (currentRoute.meta.requiresAuth && currentRoute.path !== '/login') {
            router.push('/login');
          }
        }
      }
    } else if (error.message === 'Network Error' || error.message.includes('JSON')) {
      // Handle JSON parsing errors which can happen with malformed responses
      logger.error('Network or JSON parsing error:', error.message);
      
      // Check if user has a token but might be invalid
      const token = localStorage.getItem('token');
      if (token) {
        logger.warn('Possible invalid session, clearing auth state');
        const authStore = useAuthStore();
        authStore.clearAuth();
        
        // Only redirect to login if on a protected route
        const currentRoute = router.currentRoute.value;
        if (currentRoute.meta.requiresAuth && currentRoute.path !== '/login') {
          router.push('/login');
        }
      }
    } else if (error.message && error.message.includes('validate credentials')) {
      // Handle specific "Could not validate credentials" errors
      logger.warn('Credentials validation error:', error.message);
      
      // Clear any existing token
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      
      const authStore = useAuthStore();
      if (authStore.isAuthenticated) {
        authStore.clearAuth();
      }
      
      // Determine if we should redirect based on the current route
      const currentRoute = router.currentRoute.value;
      
      // Check if this is the analyze page, which doesn't require auth
      if (currentRoute.name === 'analyze') {
        logger.debug('On analyze page - not redirecting to login');
        // Just clear token but allow user to continue
      } else if (currentRoute.meta.requiresAuth && currentRoute.path !== '/login') {
        // Only redirect for protected routes
        router.push('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Add Router and i18n
app.use(router)
app.use(i18n)

// Set token in axios if it exists in localStorage
const token = localStorage.getItem('token')
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

// After app and router are set up, initialize GA
initGA();

// Track page views on every route change
router.afterEach((to) => {
  trackPageView(to.fullPath);
});

// Mount the app
app.mount('#app')
